import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { ClipboardDocumentListIcon, EyeIcon } from '@heroicons/react/24/outline';
import PdfPreviewModal from './PdfPreviewModal';

const formatWhen = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/**
 * A paper-format PDF, generated by the server on every request.
 * `kind`: 'pdf' = the Application Form, 'assessment-pdf' = the Interview Assessment Form
 * (`sessionId` = rate it from that interview round rather than the latest).
 */
export async function fetchApplicationFormPdf(screeningId, candidateId, { kind = 'pdf', sessionId, fallbackName = 'candidate' } = {}) {
  const res = await axios.get(`/api/application-forms/screening/${screeningId}/candidates/${candidateId}/${kind}`, {
    responseType: 'blob',
    params: sessionId ? { sessionId } : undefined,
  });
  const disposition = res.headers?.['content-disposition'] || '';
  return {
    blob: new Blob([res.data], { type: 'application/pdf' }),
    fileName: /filename="([^"]+)"/.exec(disposition)?.[1] || `${fallbackName}.pdf`,
  };
}

/** The Application Form as the client's paper form (FHRM/F/003/01): previewed and downloaded as the same PDF. */
export default function ApplicationFormModal({ isOpen, onClose, screeningId, candidate }) {
  const candidateId = candidate?._id || candidate?.id;
  const { status, submittedAt } = candidate?.applicationForm || {};
  const load = useCallback(
    () =>
      fetchApplicationFormPdf(screeningId, candidateId, {
        fallbackName: `Application_Form_${(candidate?.name || 'candidate').replace(/\s+/g, '_')}`,
      }),
    [screeningId, candidateId, candidate?.name]
  );
  const when = status === 'submitted' ? `Submitted ${formatWhen(submittedAt)}`.trim() : 'In progress — not submitted yet';

  return (
    <PdfPreviewModal
      isOpen={isOpen}
      onClose={onClose}
      title="Application Form"
      subtitle={[candidate?.name || candidate?.email, when].filter(Boolean).join(' · ')}
      icon={<ClipboardDocumentListIcon className="h-5 w-5" />}
      load={load}
    />
  );
}

/** Candidates-table cell: "—", "In progress", or a View button that opens the modal. */
export function ApplicationFormCell({ candidate, screeningId }) {
  const [open, setOpen] = useState(false);
  const status = candidate?.applicationForm?.status;
  if (!status) return <span className="text-gray-400">—</span>;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1 rounded-md px-2 py-1 focus:outline-none focus:ring-1 ${
          status === 'submitted'
            ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 focus:ring-indigo-500'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 focus:ring-gray-400'
        }`}
        title={status === 'submitted' ? 'View application form' : 'The candidate has started but not submitted the form'}
      >
        {status === 'submitted' ? 'Submitted' : 'In progress'}
        <EyeIcon className="h-3.5 w-3.5" />
      </button>
      {open && (
        <ApplicationFormModal isOpen={open} onClose={() => setOpen(false)} screeningId={screeningId} candidate={candidate} />
      )}
    </>
  );
}
